import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import BrandLogo from '../components/BrandLogo';
import SupportWidget from '../components/SupportWidget';
import { 
  Home, Gift, Users, DollarSign, Settings, LogOut, 
  Calendar, Eye, ExternalLink, Sparkles, TrendingUp, Clock,
  Edit3, Plus, ChevronRight, Menu, X, Brush
} from 'lucide-react';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const formatFeePercentage = (value) => `${Number(value || 0).toFixed(2).replace('.', ',')}%`;

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard', icon: <Home className="w-5 h-5" />, label: 'Visão Geral' },
    { path: '/editor', icon: <Edit3 className="w-5 h-5" />, label: 'Editar Site' },
    { path: '/gifts', icon: <Gift className="w-5 h-5" />, label: 'Lista de Presentes' },
    { path: '/guests', icon: <Users className="w-5 h-5" />, label: 'Convidados' },
    { path: '/dashboard/convites-artes', icon: <Brush className="w-5 h-5" />, label: 'Convites & Artes' },
    { path: '/finances', icon: <DollarSign className="w-5 h-5" />, label: 'Minha carteira' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Até logo!');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-100 z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <BrandLogo size="sm" />
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E8B7B1] flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1C1C1C] truncate">{user?.name}</p>
                <p className="text-sm text-[#333333] truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-100">
            <button
              onClick={handleLogout}
              className="nav-item w-full text-red-500 hover:bg-red-50 hover:text-red-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [site, setSite] = useState(null);
  const [balance, setBalance] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [guestStats, setGuestStats] = useState({ total: 0, confirmed: 0, pending: 0, declined: 0 });
  const [partnerCode, setPartnerCode] = useState('');
  const [redeemingPartnerCode, setRedeemingPartnerCode] = useState(false);
  const [cardFeeResponsibility, setCardFeeResponsibility] = useState('payer');
  const [savingCardFeeResponsibility, setSavingCardFeeResponsibility] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const resolvedResponsibility =
      site?.card_fee_responsibility === 'couple'
        ? 'couple'
        : user?.preferred_card_fee_responsibility === 'couple'
          ? 'couple'
          : 'payer';

    setCardFeeResponsibility(resolvedResponsibility);
  }, [site?.card_fee_responsibility, user?.preferred_card_fee_responsibility]);

  const fetchDashboardData = async () => {
    try {
      const [siteRes, balanceRes, giftsRes, rsvpsRes, guestsRes] = await Promise.all([
        axios.get(`${API}/sites/my`, { withCredentials: true }),
        axios.get(`${API}/balance`, { withCredentials: true }),
        axios.get(`${API}/gifts`, { withCredentials: true }),
        axios.get(`${API}/rsvps`, { withCredentials: true }),
        axios.get(`${API}/guests`, { withCredentials: true }),
      ]);
      
      setSite(normalizeObjectStrings(siteRes.data));
      setBalance(normalizeObjectStrings(balanceRes.data));
      setGifts(normalizeObjectStrings(giftsRes.data || []));
      setRsvps(normalizeObjectStrings(rsvpsRes.data || []));
      setGuestStats(normalizeObjectStrings(guestsRes.data?.stats || { total: 0, confirmed: 0, pending: 0, declined: 0 }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateSubscription = async () => {
    try {
      await axios.post(`${API}/subscription/activate`, {}, { withCredentials: true });
      toast.success('Plano grátis ativado com sucesso!');
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao ativar plano');
    }
  };

  const handleRedeemPartnerCode = async () => {
    const normalizedCode = String(partnerCode || '').trim().toUpperCase();
    if (!normalizedCode) {
      toast.error('Digite um código de parceria para continuar.');
      return;
    }

    setRedeemingPartnerCode(true);
    try {
      const response = await axios.post(
        `${API}/partner-codes/redeem`,
        { code: normalizedCode },
        { withCredentials: true }
      );
      await refreshUser();
      setPartnerCode('');
      toast.success(response.data?.message || 'Código aplicado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível aplicar o código agora.');
    } finally {
      setRedeemingPartnerCode(false);
    }
  };

  const handleCardFeeResponsibilityChange = async (value) => {
    const normalizedValue = value === 'couple' ? 'couple' : 'payer';
    const previousValue = cardFeeResponsibility;

    setCardFeeResponsibility(normalizedValue);
    setSavingCardFeeResponsibility(true);

    try {
      const response = await axios.put(
        `${API}/account/card-fee-responsibility`,
        { card_fee_responsibility: normalizedValue },
        { withCredentials: true }
      );

      const persistedValue = response.data?.card_fee_responsibility === 'couple' ? 'couple' : 'payer';
      setCardFeeResponsibility(persistedValue);
      setSite((currentSite) => (
        currentSite
          ? { ...currentSite, card_fee_responsibility: persistedValue }
          : currentSite
      ));
      await refreshUser();
      toast.success('Preferência da taxa do cartão atualizada.');
    } catch (error) {
      setCardFeeResponsibility(previousValue);
      toast.error(error.response?.data?.detail || 'Não foi possível atualizar a taxa do cartão agora.');
    } finally {
      setSavingCardFeeResponsibility(false);
    }
  };

  const daysUntilWedding = site?.wedding_date 
    ? Math.max(0, Math.ceil((new Date(site.wedding_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const purchasedGifts = gifts.filter(g => !g.is_available).length;
  const confirmedGuests = site?.rsvp_mode === 'predefined'
    ? Number(guestStats.confirmed || 0)
    : rsvps.filter(r => r.attending).length;
  const pendingCardReleaseBalance = Number(balance?.pending_card_release_balance || 0);
  const effectiveFeePercentage = Number(user?.effective_fee_percentage ?? 3.59);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333] font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-6 h-6 text-[#1C1C1C]" />
            </button>
            <div className="flex-1" />
            {site && (
              <a
                href={`/s/${site.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#1C1C1C] hover:text-[#C8A97E] transition-colors duration-200"
              >
                <Eye className="w-5 h-5" />
                <span className="hidden sm:inline">Ver Meu Site</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Olá, {user?.name?.split(' ')[0]}! {daysUntilWedding !== null && daysUntilWedding <= 30 && ' (reta final)'}
            </h1>
            {daysUntilWedding !== null && (
              <p className="text-lg text-[#333333] mt-2">
                Faltam <span className="font-bold text-[#C8A97E]">{daysUntilWedding}</span> dias para o grande dia!
              </p>
            )}
          </div>

          {/* Subscription Banner */}
          {!user?.has_subscription && (
            <div className="mb-8 bg-gradient-to-r from-[#FDEBE8] to-[#E8B7B1]/40 rounded-2xl p-6 md:p-8 border border-[#E8B7B1]/35">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-6 h-6 text-[#C8A97E]" />
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
                      Ative seu plano grátis
                    </h3>
                  </div>
                  <p className="text-[#333333]">
                    Comece sem assinatura mensal e pague apenas <span className="font-bold text-[#C8A97E]">3,59%</span> por presente confirmado.
                  </p>
                </div>
                <Link to="/planos">
                  <Button
                    className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full px-8 font-bold transition-colors duration-300"
                    data-testid="activate-subscription-btn"
                  >
                    Entender condições
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Plan Info Banner */}
          {user?.has_subscription && (
            <div className="mb-8 rounded-2xl border border-[#C8A97E]/30 bg-[#C8A97E]/10 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C8A97E] flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-[#1C1C1C]">
                        Plano Grátis
                      </p>
                      <p className="text-sm text-[#333333]">
                        Taxa da plataforma: {formatFeePercentage(effectiveFeePercentage)} por presente confirmado
                      </p>
                    </div>
                  </div>
                  <Link to="/planos">
                    <Button variant="outline" className="rounded-full text-sm">
                      Ver detalhes da taxa
                    </Button>
                  </Link>
                </div>

                <div className="rounded-2xl border border-[#E8B7B1]/35 bg-white/80 p-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="max-w-2xl">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">
                        Taxa do cartão
                      </p>
                      <h3 className="mt-2 text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                        Escolha quem assume o acréscimo do cartão
                      </h3>
                      <p className="mt-1 text-sm text-[#333333]">
                        Defina se a taxa do cartão será repassada para quem compra o presente ou absorvida pelo casal no valor líquido recebido.
                      </p>
                      <p className="mt-2 text-xs text-[#666666]">
                        {site
                          ? 'Essa mudança atualiza o checkout do seu site.'
                          : 'Essa escolha já será aplicada quando você criar seu site.'}
                      </p>
                    </div>

                    <div className="grid w-full max-w-2xl gap-3 md:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleCardFeeResponsibilityChange('payer')}
                        disabled={savingCardFeeResponsibility}
                        className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                          cardFeeResponsibility === 'payer'
                            ? 'border-[#C8A97E] bg-[#FDF7F2] shadow-sm'
                            : 'border-slate-200 bg-white hover:border-[#E8B7B1]'
                        } ${savingCardFeeResponsibility ? 'cursor-wait opacity-70' : ''}`}
                      >
                        <p className="text-sm font-semibold text-[#1C1C1C]">
                          Quem compra o presente paga
                        </p>
                        <p className="mt-1 text-xs text-[#666666]">
                          O convidado vê o acréscimo do cartão no checkout.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleCardFeeResponsibilityChange('couple')}
                        disabled={savingCardFeeResponsibility}
                        className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                          cardFeeResponsibility === 'couple'
                            ? 'border-[#C8A97E] bg-[#FDF7F2] shadow-sm'
                            : 'border-slate-200 bg-white hover:border-[#E8B7B1]'
                        } ${savingCardFeeResponsibility ? 'cursor-wait opacity-70' : ''}`}
                      >
                        <p className="text-sm font-semibold text-[#1C1C1C]">
                          O casal assume essa taxa
                        </p>
                        <p className="mt-1 text-xs text-[#666666]">
                          O convidado paga só o valor do presente e o acréscimo sai do valor líquido recebido.
                        </p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 rounded-2xl border border-[#E8B7B1]/35 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">
                  Parcerias e parceiros
                </p>
                <h3 className="mt-2 text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Ative seu código de parceria
                </h3>
                <p className="mt-1 text-sm text-[#333333]">
                  Digite seu código para ativar a condição especial da sua conta e aplicar a nova taxa aos próximos presentes.
                </p>
              </div>

              {user?.fee_benefit_active ? (
                <div className="rounded-2xl border border-green-200 bg-green-50 px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-green-700">Parceria ativa</p>
                  <p className="mt-1 text-lg font-bold text-[#1C1C1C]">
                    {user?.fee_benefit_label || 'Condição especial aplicada'}
                  </p>
                  <p className="text-sm text-[#333333]">
                    Código: <span className="font-semibold">{user?.fee_benefit_code || '-'}</span>
                  </p>
                  <p className="text-sm text-[#333333]">
                    Nova taxa da conta: <span className="font-semibold text-green-700">{formatFeePercentage(effectiveFeePercentage)}</span>
                  </p>
                </div>
              ) : (
                <div className="flex w-full max-w-xl flex-col gap-3 sm:flex-row">
                  <Input
                    value={partnerCode}
                    onChange={(event) => setPartnerCode(event.target.value.toUpperCase())}
                    placeholder="Digite seu código de parceria"
                    className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F6F1EF]"
                  />
                  <Button
                    onClick={handleRedeemPartnerCode}
                    disabled={redeemingPartnerCode}
                    className="h-12 rounded-xl bg-[#E8B7B1] px-6 text-white hover:bg-[#DFA8A1]"
                  >
                    {redeemingPartnerCode ? 'Aplicando...' : 'Aplicar código'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* No Site Banner */}
          {user?.has_subscription && !site && (
            <div className="mb-8 bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                    Crie seu site de casamento
                  </h3>
                  <p className="text-[#333333] mt-1">
                    Escolha um template e comece a personalizar agora mesmo!
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/editor')}
                  className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full px-8 font-bold transition-colors duration-300"
                  data-testid="create-site-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Criar Site
                </Button>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#E8B7B1]/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#1C1C1C]" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-sm text-[#333333] uppercase tracking-wider">Saldo Disponível</p>
              <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="balance-value">
                R$ {balance?.available_balance?.toFixed(2) || '0,00'}
              </p>
              {pendingCardReleaseBalance > 0 && (
                <p className="mt-2 text-xs text-[#333333]">
                  R$ {pendingCardReleaseBalance.toFixed(2)} em liberação do cartão
                </p>
              )}
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#C8A97E]/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#C8A97E]" />
                </div>
              </div>
              <p className="text-sm text-[#333333] uppercase tracking-wider">Presentes</p>
              <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="gifts-count">
                {purchasedGifts}/{gifts.length}
              </p>
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-[#333333] uppercase tracking-wider">Confirmados</p>
              <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="guests-count">
                {confirmedGuests}
              </p>
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-[#333333] uppercase tracking-wider">Dias Restantes</p>
              <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="days-count">
                {daysUntilWedding ?? '-'}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/editor" className="card-dashboard hover-lift group" data-testid="quick-action-editor">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                    {site ? 'Editar Site' : 'Criar Site'}
                  </h3>
                  <p className="text-[#333333] text-sm mt-1">
                    {site ? 'Personalize cores, fotos e textos' : 'Escolha um template e comece'}
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-[#333333] group-hover:text-[#C8A97E] transition-colors duration-200" />
              </div>
            </Link>

            <Link to="/gifts" className="card-dashboard hover-lift group" data-testid="quick-action-gifts">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                    Lista de Presentes
                  </h3>
                  <p className="text-[#333333] text-sm mt-1">
                    Adicione e gerencie presentes
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-[#333333] group-hover:text-[#C8A97E] transition-colors duration-200" />
              </div>
            </Link>

            <Link to="/guests" className="card-dashboard hover-lift group" data-testid="quick-action-guests">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                    Convidados
                  </h3>
                  <p className="text-[#333333] text-sm mt-1">
                    Veja quem confirmou presença
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-[#333333] group-hover:text-[#C8A97E] transition-colors duration-200" />
              </div>
            </Link>

            <Link to="/dashboard/convites-artes" className="card-dashboard hover-lift group" data-testid="quick-action-creative">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                    Convites & Artes
                  </h3>
                  <p className="text-[#333333] text-sm mt-1">
                    Crie materiais visuais para compartilhar
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-[#333333] group-hover:text-[#C8A97E] transition-colors duration-200" />
              </div>
            </Link>
          </div>

          {/* Recent Activity */}
          {(gifts.length > 0 || rsvps.length > 0) && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Atividade Recente
              </h2>
              <div className="card-dashboard">
                <div className="space-y-4">
                  {gifts.filter(g => !g.is_available).slice(0, 3).map((gift, i) => (
                    <div key={i} className="flex items-center gap-4 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                      <div className="w-10 h-10 rounded-full bg-[#C8A97E]/10 flex items-center justify-center">
                        <Gift className="w-5 h-5 text-[#C8A97E]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1C1C1C]">{gift.purchased_by} presenteou</p>
                        <p className="text-sm text-[#333333]">{gift.name} - R$ {gift.price.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                  {rsvps.slice(0, 3).map((rsvp, i) => (
                    <div key={i} className="flex items-center gap-4 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1C1C1C]">{rsvp.guest_name}</p>
                        <p className="text-sm text-[#333333]">
                          {rsvp.attending ? `Confirmou presença (${rsvp.guests_count} pessoa${rsvp.guests_count > 1 ? 's' : ''})` : 'Não poderá comparecer'}
                        </p>
                      </div>
                    </div>
                  ))}
                  {gifts.length === 0 && rsvps.length === 0 && (
                    <p className="text-center text-[#333333] py-4">
                      Nenhuma atividade ainda. Compartilhe seu site!
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      <SupportWidget />
    </div>
  );
}



